import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditInventoryOwner,
  InventoryOwner,
} from '../../../shared/models/api/inventory-owner.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryOwnerService } from '../../../shared/services/api/inventory-owner.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { EditInventoryOwnerFormValue } from './inventory-owner-edit.models';

@Component({
  selector: 'opa-inventory-owner-edit',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-owner-edit.component.html',
  styleUrl: './inventory-owner-edit.component.scss',
})
export class InventoryOwnerEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryOwnerService = inject(InventoryOwnerService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) inventoryOwner: InventoryOwner | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryOwnerEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.inventoryOwner);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.inventoryOwner) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const owner = this.formValueToOwner(formValue);
    this.subSink.sink = this.inventoryOwnerService
      .editInventoryOwner(this.inventoryOwner.id, owner)
      .pipe(
        tap({
          next: (editedOwner) => {
            this.toastManager.addMessage({
              text: `Owner '${editedOwner.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditInventoryOwnerFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditInventoryOwnerFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
    });

    return form;
  }

  private formValueToOwner(form: EditInventoryOwnerFormValue): EditInventoryOwner {
    const inventoryOwner: EditInventoryOwner = {
      description: form.description!,
      name: this.inventoryOwner!.name,
    };
    return inventoryOwner;
  }

  private resetForm(inventoryOwner?: InventoryOwner): void {
    this.form.reset();
    if (!inventoryOwner) return;

    this.form.patchValue({
      description: inventoryOwner.description,
    });
  }
}
